/**
 * Initializes WPD Page Manager.
 */
function init(wpd, wpdDocument, dataEntryVM) {
	const originalNext = wpd.PageManager.prototype.next;
	const originalPrevious = wpd.PageManager.prototype.previous;

	wpd.PageManager.prototype.next = function () {
		const self = this;
		originalNext.call(self);
		wpd.tagGroups.refreshAll(false, true);
	};

	wpd.PageManager.prototype.previous = function () {
		const self = this;
		originalPrevious.call(self);
		wpd.tagGroups.refreshAll(false, true);
	};

	// select text extraction item after switching files (for subsequent loads)
	// skipRaster: the caller has already rasterized this exact page, so do the
	// surrounding bookkeeping but leave the bitmap that is already on screen.
	wpd.PageManager.prototype.renderPage = function (
		pageNumber,
		isQualityChange,
		skipRaster = false,
	) {
		// update previous and next buttons
		this.updateButtons(pageNumber);

		return new Promise(async (resolve, reject) => {
			if (skipRaster) {
				// _pageRenderer would normally close this once the image lands
				wpd.busyNote.close();
				resolve();
			} else {
				const page = await this.getPage(pageNumber);
				await this._pageRenderer(page, isQualityChange, resolve, reject);
			}

			// reset axis tree buttons
			wpdDocument
				.getElementById("graph-add-button")
				?.classList.remove("pressed-button");
			wpdDocument
				.getElementById("table-add-button")
				?.classList.remove("pressed-button");

			if (!wpd.tree.getRootTreeSelectedPath()) {
				await wpd.tree.selectPath(null);
			}
		});
	};

	// add current page loading logic
	wpd.PageManager.prototype.loadPageData = function (data) {
		this.axesByPage = data.axes || {};
		this.datasetsByPage = data.datasets || {};
		this.measurementsByPage = data.measurements || {};
		this.customLabelsByPage = data.pageLabels || {};
		this.rotationsByPage = data.rotations || {};
		this.curPage = data.currentPage ?? 1;

		// set graphics widget rotation if the loaded page has rotation data
		if (Object.keys(this.rotationsByPage).length) {
			wpd.graphicsWidget.setRotation(this.getRotation());
			wpd.graphicsWidget.rotateAndResize();
		}
	};

	// disable previous and next buttons when they will be out of bounds
	wpd.PageManager.prototype.switch = async function (
		pageNumber = 1,
		skipRaster = false,
	) {
		wpd.busyNote.show();

		const parsedPageNumber = parseInt(pageNumber, 10);

		// update previous and next buttons
		this.updateButtons(parsedPageNumber);

		this.curPage = parsedPageNumber;

		// udpate select value for calls from other controls
		this.$pageSelector.value = parsedPageNumber;

		this._resetRelabelPopup();

		// clear tag group ui elements
		wpd.tagGroups.hideControls();

		// unselect all data points
		wpd.tree.getActiveDataset()?.unselectAll();

		// unselect axis tree item;
		const axisPath = wpd.tree.getAxisTreeSelectedPath();
		if (axisPath) {
			wpd.tree.selectAxisPath(null);
		}

		if (dataEntryVM.pointGroupOverhaul) {
			// clear active study branch keys
			wpd.tagGroups.setActiveKeys({});
		}

		const result = await this.renderPage(parsedPageNumber, false, skipRaster);

		// re-select root path
		const rootPath = wpd.tree.getRootTreeSelectedPath();
		if (rootPath) {
			wpd.tree.selectPath(rootPath);
		}

		return result;
	};

	wpd.PageManager.prototype.updateButtons = function (pageNumber) {
		// short-circuit if the given page number is out of bounds
		if (!this._validatePageNumber(pageNumber)) {
			wpd.busyNote.close();
			wpd.messagePopup.show("Error", "Invalid page number.");
			return false;
		}

		const previousButton = wpdDocument.getElementById("previous-page-button");
		const nextButton = wpdDocument.getElementById("next-page-button");

		// reset previous and next buttons
		previousButton.disabled = false;
		previousButton.classList.remove("disabled");
		nextButton.disabled = false;
		nextButton.classList.remove("disabled");

		switch (true) {
			case !this._validatePageNumber(pageNumber - 1):
				// previous page is out of bounds
				previousButton.disabled = true;
				previousButton.classList.add("disabled");
				break;
			case !this._validatePageNumber(pageNumber + 1):
				// next page is out of bounds
				nextButton.disabled = true;
				nextButton.classList.add("disabled");
				break;
		}
	};
}

export { init };
